// oxlint-disable-next-line no-restricted-imports
import { matchPath } from 'react-router';
import { enableSignedSAMLAuthnRequests } from '@gonfalon/dogfood-flags/dogfood-flags';
import { http, jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import invariant from 'tiny-invariant';

import { getDestinationUrl } from '#actions/navigation';
import type { MFARecoveryCodes } from '#utils/accounts/records/Security';
import { createMFARecoveryCodes } from '#utils/accounts/records/Security';
import type { SignupForm } from '#utils/accounts/records/SignupForm';
import type { LoginInfo } from '#utils/loginUtils';
import type { ForgotPassword, ResetPassword } from '#utils/resetPasswordUtils';

function acceptSessionRedirect(response: Response) {
  if (response.status === 303 || response.status === 302) {
    return Promise.resolve(response);
  }
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }
  return Promise.reject(response);
}

// http.* rejects non-2xx responses before callers can inspect redirect Location headers.
// Re-run acceptSessionRedirect on those rejections so 302/303 login responses resolve.
// Session login requests use the default redirect:"follow" so Set-Cookie from a 303 chain
// is applied before callers hard-navigate via assignPostLoginRedirect (redirect:"manual"
// leaves the cookie unset on the subsequent window.location.replace).
function chainSessionRedirect(promise: Promise<Response>) {
  return promise
    .then(acceptSessionRedirect)
    .catch((response: unknown) => {
      if (response instanceof Response) {
        return acceptSessionRedirect(response);
      }
      return Promise.reject(response);
    })
    .catch(jsonToImmutableError);
}

export function login(info: LoginInfo | SignupForm) {
  return chainSessionRedirect(
    http.put('/internal/account/login', {
      body: info,
    }),
  );
}

export function loginV2(info: LoginInfo, redirectPath?: string) {
  const { username, password, accountId, oauth } = info;
  const convertUsernameToEmail = {
    email: username,
    password,
    accountId,
    oauth,
  };
  const body = enableSignedSAMLAuthnRequests()
    ? { ...convertUsernameToEmail, redirect: redirectPath || getDestinationUrl() }
    : convertUsernameToEmail;
  return chainSessionRedirect(
    http.post('/internal/account/login', {
      body,
    }),
  );
}

export function getLoginPage(email: string, redirectPath?: string) {
  const body: { email: string; redirect?: string } = { email };

  body.redirect = redirectPath || getDestinationUrl();

  return http
    .put('/internal/account/login2', {
      body,
    })
    .catch((err) => jsonToImmutableError(err));
}

export function escalateSession(info: LoginInfo) {
  return http
    .post('/internal/account/session/escalate', {
      body: info,
    })
    .catch(jsonToImmutableError);
}

export function loginWithMFA(token: string, passcode: string, accountID?: string, redirectPath?: string) {
  return chainSessionRedirect(
    http.post('/internal/account/session/mfa', {
      body: {
        accountID,
        token,
        passcode,
        ...(redirectPath ? { redirect: redirectPath } : {}),
      },
    }),
  );
}

export function confirmEnrollmentMFA(token: string, passcode: string): Promise<MFARecoveryCodes> {
  return http
    .post('/internal/login/mfa/confirm', {
      body: { token, passcode },
    })
    .then(jsonToImmutable)
    .then(createMFARecoveryCodes)
    .catch(jsonToImmutableError);
}

export function loginWithMFARecovery(token: string, recoveryCode: string, accountID?: string) {
  return http
    .post('/internal/account/session/mfa-recovery', {
      body: { accountID, token, recoveryCode },
    })
    .catch(jsonToImmutableError);
}

export function forgotPassword(reset: ForgotPassword) {
  return http
    .post('/internal/forgot', {
      body: JSON.stringify(reset.toJS()),
    })
    .catch(jsonToImmutableError);
}

export function resetPassword(reset: ResetPassword) {
  const match = matchPath('/reset/:passwordResetToken', window.location.pathname);
  invariant(match?.params.passwordResetToken, 'Expected passwordResetToken route param');

  return http
    .post(`/internal/reset/${match.params.passwordResetToken}`, {
      body: JSON.stringify(reset.toJS()),
    })
    .catch(jsonToImmutableError);
}

export function logout() {
  return http.delete('/internal/account/session').catch(jsonToImmutableError);
}
